(() => {
  if (customElements.get('scroll-nav')) {
    return;
  }

  class ScrollNav extends HTMLElement {
    constructor() {
      super();

      this.sectionLinks = [];
      this.navItems = [];
      this.scrollRaf = null;

      this.handleNavClick = this.handleNavClick.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handleResize = this.handleResize.bind(this);
    }

    connectedCallback() {
      this.initNavigation();
    }

    disconnectedCallback() {
      this.teardownNavigation();
    }

    initNavigation() {
      const navAnchors = Array.from(
        this.querySelectorAll('.section__sidebar .nav-item > a, .section__sidebar .js-nav-item > a')
      );

      if (!navAnchors.length) {
        return;
      }

      this.sectionLinks = navAnchors
        .map((anchor) => {
          const href = anchor.getAttribute('href');
          if (!href || href.charAt(0) !== '#') {
            return null;
          }

          const id = href.slice(1);
          const selector = this.escapeSelector(`#${id}`);
          const target = this.querySelector(selector) || document.querySelector(selector);

          if (!target) {
            return null;
          }

          const navItem = anchor.closest('.nav-item') || anchor.parentElement;

          if (navItem) {
            navItem.dataset.navId = id;
          }

          return {
            anchor,
            navItem,
            target,
            id,
            top: 0,
          };
        })
        .filter(Boolean);

      if (!this.sectionLinks.length) {
        return;
      }

      this.navItems = this.sectionLinks
        .map(({ navItem }) => navItem)
        .filter(Boolean);

      this.sectionLinks.forEach(({ anchor }) => {
        anchor.addEventListener('click', this.handleNavClick);
      });

      this.updateSectionPositions();
      this.setActiveByScroll();

      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('resize', this.handleResize);
    }

    teardownNavigation() {
      this.sectionLinks.forEach(({ anchor }) => {
        anchor.removeEventListener('click', this.handleNavClick);
      });

      window.removeEventListener('scroll', this.handleScroll);
      window.removeEventListener('resize', this.handleResize);

      if (this.scrollRaf) {
        cancelAnimationFrame(this.scrollRaf);
        this.scrollRaf = null;
      }
    }

    handleNavClick(event) {
      const anchor = event.currentTarget;
      const href = anchor.getAttribute('href');

      if (!href || href.charAt(0) !== '#') {
        return;
      }

      const targetId = href.slice(1);
      const link = this.sectionLinks.find(({ id }) => id === targetId);

      if (!link) {
        return;
      }

      event.preventDefault();

      this.updateSectionPositions();

      const baseOffset = this.getOffset();
      const currentPosition = window.scrollY + baseOffset + 1;

      let currentIndex = 0;
      for (let i = 0; i < this.sectionLinks.length; i += 1) {
        const current = this.sectionLinks[i];
        const next = this.sectionLinks[i + 1];

        if (currentPosition >= current.top && (!next || currentPosition < next.top)) {
          currentIndex = i;
          break;
        }
      }

  const targetIndex = this.sectionLinks.indexOf(link);
  const goingDown = targetIndex > currentIndex;
  const isWideViewport = window.innerWidth >= 1024;
  const offset = goingDown && isWideViewport ? this.getAdjustedOffset(baseOffset) : baseOffset;
      const top = link.top - offset;

      window.scrollTo({
        top,
        behavior: 'smooth',
      });

      this.setActiveNav(targetId);
    }

    handleScroll() {
      if (this.scrollRaf) {
        return;
      }

      this.scrollRaf = window.requestAnimationFrame(() => {
        this.scrollRaf = null;
        this.setActiveByScroll();
      });
    }

    handleResize() {
      this.updateSectionPositions();
      this.setActiveByScroll();
    }

    updateSectionPositions() {
      this.sectionLinks.forEach((link) => {
        link.top = this.getOffsetTop(link.target);
      });

      this.sectionLinks.sort((a, b) => a.top - b.top);
    }

    setActiveByScroll() {
      if (!this.sectionLinks.length) {
        return;
      }

      const scrollPosition = window.scrollY + this.getOffset() + 1;

      let activeLink = this.sectionLinks[0];

      for (let i = 0; i < this.sectionLinks.length; i += 1) {
        const current = this.sectionLinks[i];
        const next = this.sectionLinks[i + 1];

        if (scrollPosition >= current.top && (!next || scrollPosition < next.top)) {
          activeLink = current;
          break;
        }
      }

      this.setActiveNav(activeLink?.id);
    }

    setActiveNav(id) {
      if (!id) {
        return;
      }

      this.navItems.forEach((item) => {
        if (item) {
          item.classList.remove('active');
        }
      });

      const link = this.sectionLinks.find(({ id: linkId }) => linkId === id);

      if (link && link.navItem) {
        link.navItem.classList.add('active');
      }
    }

    getOffsetTop(element) {
      return element.getBoundingClientRect().top + window.scrollY;
    }

    getOffset() {
      const attributeOffset = parseInt(this.getAttribute('offset') || this.dataset.offset || '0', 10);
      const customOffset = Number.isNaN(attributeOffset) ? 0 : attributeOffset;
      const header =
        document.querySelector('.header-group') ||
        document.querySelector('.section-header-group') ||
        document.querySelector('header');
      const headerHeight = header ? header.offsetHeight : 0;

      return customOffset + headerHeight;
    }

    getAdjustedOffset(baseOffset) {
      const headerWrapperHeight = this.getHeaderWrapperHeight();

      if (!headerWrapperHeight) {
        return baseOffset;
      }

      return Math.max(0, baseOffset - headerWrapperHeight);
    }

    getHeaderWrapperHeight() {
      const headerWrapper = document.querySelector('.shopify-section-group-header-group.header-wrapper');

      return headerWrapper ? headerWrapper.offsetHeight : 0;
    }

    escapeSelector(selector) {
      if (window.CSS && typeof window.CSS.escape === 'function') {
        const id = selector.replace('#', '');
        return `#${window.CSS.escape(id)}`;
      }

      return selector;
    }
  }

  customElements.define('scroll-nav', ScrollNav);
})();
